import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { usePerformanceInboxQuery } from '@/client/api/marketing/radar';
import { PLATFORM_LABEL, type SocialPlatform } from '@/client/types/marketing';
import { EmptyState, KpiChange, Panel } from './components/marketing-ui';
import { changeVsPrior, formatCompact } from './posting-insights-data';
import { HEAT_DOW, dailyVolume, formatDuration, heatGrid, responseTargets } from './inbox-analytics';

const RANGES = [7, 30, 90] as const;
const COLORS = { received: '#10b981', sent: '#3b82f6', read: '#8b5cf6' };

const platformLabel = (p: string) => PLATFORM_LABEL[p as SocialPlatform] ?? p;
const shortDay = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** Inbox (DM) analytics: Zernio's Inbox add-on, proxied live by ff-api. */
const InboxAnalyticsPage = () => {
  const [range, setRange] = useState<number>(30);
  const [platform, setPlatform] = useState('');
  const q = usePerformanceInboxQuery({ range_days: range, ...(platform ? { platform } : {}) });
  const data = q.data;

  const days = useMemo(
    () => (data ? dailyVolume(data.volume, data.window.from, data.window.to) : []),
    [data],
  );
  const targets = useMemo(() => responseTargets(data?.response_time ?? null), [data]);
  const heat = useMemo(() => heatGrid(data?.heatmap ?? null), [data]);
  // Options come from an unfiltered load; a filtered one only knows its own platform.
  const [platforms, setPlatforms] = useState<string[]>([]);
  useEffect(() => {
    if (!platform && data?.volume) setPlatforms(data.volume.byPlatform.map((r) => r.platform));
  }, [platform, data]);

  if (q.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" aria-label="Loading" />
      </div>
    );
  }
  if (!data) return <EmptyState label="Could not load inbox analytics." />;

  const s = data.volume?.summary;
  const p = data.prior_volume?.summary;
  const rt = data.response_time?.summary;
  const replied = rt?.sampleSize ?? 0;
  const waiting = Math.max(0, (s?.received ?? 0) - replied);
  const kpis = [
    { label: 'Received', value: s?.received, prior: p?.received },
    { label: 'Sent', value: s?.sent, prior: p?.sent },
    { label: 'Read', value: s?.read, prior: p?.read },
    { label: 'Failed', value: s?.failed, prior: p?.failed },
    { label: 'Conversations', value: s?.uniqueConversations, prior: p?.uniqueConversations },
  ];
  const errors = Object.entries(data.errors);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Platform"
          className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
        >
          <option value="">All platforms</option>
          {platforms.map((pl) => (
            <option key={pl} value={pl}>
              {platformLabel(pl)}
            </option>
          ))}
        </select>
        <select
          aria-label="Range"
          className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm"
          value={range}
          onChange={(e) => setRange(Number(e.target.value))}
        >
          {RANGES.map((r) => (
            <option key={r} value={r}>
              Last {r} days
            </option>
          ))}
        </select>
        <span className="text-xs text-gray-400">
          {data.window.from} – {data.window.to} · live from the vendor inbox · deltas vs the prior {data.window.range_days} days
        </span>
      </div>

      {errors.length > 0 ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Some blocks did not load ({errors.map(([k, v]) => `${k}: ${v}`).join(', ')}). The vendor's
          Inbox add-on may have lapsed; the rest of the page is still current.
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-7">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
            <div className="text-xs text-gray-500">{k.label}</div>
            <div className="text-xl font-semibold tabular-nums text-gray-900">{formatCompact(k.value)}</div>
            <KpiChange value={k.value ?? null} change={k.value == null ? null : changeVsPrior(k.value, k.prior ?? 0)} />
          </div>
        ))}
        <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
          <div className="text-xs text-gray-500">Median response</div>
          <div className="text-xl font-semibold tabular-nums text-gray-900">{formatDuration(rt?.medianSeconds)}</div>
          <span className="text-xs text-gray-400">p90 {formatDuration(rt?.p90Seconds)}</span>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
          <div className="text-xs text-gray-500">Reply rate</div>
          <div className="text-xl font-semibold tabular-nums text-gray-900">
            {s?.received ? `${Math.round((replied / s.received) * 100)}%` : '—'}
          </div>
          <span className={`text-xs ${waiting > 0 ? 'text-red-600' : 'text-gray-400'}`}>{waiting} unanswered</span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Messages over time">
          <p className="-mt-2 mb-2 text-xs text-gray-500">Received vs sent vs read per day (UTC)</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={days} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                <Tooltip labelFormatter={(d: string) => shortDay(d)} />
                <Legend />
                {(['received', 'sent', 'read'] as const).map((k) => (
                  <Line key={k} type="monotone" dataKey={k} name={k[0].toUpperCase() + k.slice(1)} stroke={COLORS[k]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Messages per platform">
          <p className="-mt-2 mb-2 text-xs text-gray-500">Received + sent in this window</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={(data.volume?.byPlatform ?? []).map((r) => ({ ...r, name: platformLabel(r.platform) }))}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                <Tooltip />
                <Legend />
                <Bar dataKey="received" name="Received" stackId="v" fill={COLORS.received} />
                <Bar dataKey="sent" name="Sent" stackId="v" fill={COLORS.sent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel
          title="Response time"
          action={<span className="text-sm font-semibold tabular-nums">{formatDuration(rt?.medianSeconds)} median</span>}
        >
          <p className="-mt-2 mb-3 text-xs text-gray-500">
            Time to the first reply after a customer message · {replied} replied ·{' '}
            <span className={waiting > 0 ? 'text-red-600' : ''}>{waiting} unanswered</span>
          </p>
          <div className="mb-3 grid grid-cols-3 gap-2">
            {targets.map((t) => (
              <div
                key={t.label}
                className={`rounded-lg border px-3 py-2 ${
                  t.pct == null ? 'border-gray-100' : t.met ? 'border-emerald-200 bg-emerald-50/60' : 'border-red-200 bg-red-50/60'
                }`}
              >
                <div className="text-xs text-gray-500">{t.label}</div>
                <div className={`text-lg font-semibold ${t.pct == null ? 'text-gray-400' : t.met ? 'text-emerald-700' : 'text-red-700'}`}>
                  {t.pct == null ? '—' : `${t.pct}%`}
                </div>
                <div className="text-[11px] text-gray-400">target {t.target}%</div>
              </div>
            ))}
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.response_time?.histogram ?? []} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                <Tooltip />
                <Bar dataKey="count" name="Replies" fill="#f59e0b" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Top accounts by volume">
          {(data.top_accounts?.accounts ?? []).length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500">
                  <tr>
                    <th className="py-1.5 font-medium">Account</th>
                    <th className="py-1.5 text-right font-medium">Received</th>
                    <th className="py-1.5 text-right font-medium">Sent</th>
                    <th className="py-1.5 text-right font-medium">Convos</th>
                    <th className="py-1.5 text-right font-medium">Replied</th>
                    <th className="py-1.5 text-right font-medium">Median resp.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_accounts!.accounts.map((a) => (
                    <tr key={a.accountId} className="border-t border-gray-100">
                      <td className="py-1.5">
                        <span className="font-medium text-gray-900">{a.displayName || a.username || a.accountId}</span>
                        <span className="ml-1 text-xs text-gray-400">
                          {platformLabel(a.platform)}
                          {a.username ? ` · @${a.username}` : ''}
                        </span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{a.received}</td>
                      <td className="py-1.5 text-right tabular-nums">{a.sent}</td>
                      <td className="py-1.5 text-right tabular-nums">{a.conversations}</td>
                      <td className="py-1.5 text-right tabular-nums">{a.repliedCount}</td>
                      <td className="py-1.5 text-right tabular-nums">{formatDuration(a.medianResponseSeconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-gray-400">
                Every connected account, duplicates included: a page connected twice appears twice.
              </p>
            </div>
          ) : (
            <EmptyState label="No inbox activity on any account in this window." />
          )}
        </Panel>

        <Panel title="Messages by source">
          <p className="-mt-2 mb-2 text-xs text-gray-500">
            Who produced each message: contact = customer, platform = sent from the native app, others = our tooling
          </p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.source_breakdown?.sources ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="source" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                <Tooltip />
                <Legend />
                <Bar dataKey="received" name="Received" stackId="s" fill={COLORS.received} />
                <Bar dataKey="sent" name="Sent" stackId="s" fill={COLORS.sent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="When messages land">
          <p className="-mt-2 mb-2 text-xs text-gray-500">Received + sent by day × hour (UTC = Accra time)</p>
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0.5 text-[10px] text-gray-400">
              <thead>
                <tr>
                  <th />
                  {Array.from({ length: 24 }, (_, h) => (
                    <th key={h} className="font-normal">
                      {h % 3 === 0 ? `${h}h` : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heat.grid.map((row, d) => (
                  <tr key={HEAT_DOW[d]}>
                    <th className="pr-1 text-left font-normal">{HEAT_DOW[d]}</th>
                    {row.map((c, h) => {
                      const n = c.received + c.sent;
                      return (
                        <td
                          key={h}
                          title={`${HEAT_DOW[d]} ${h}:00 · ${c.received} received, ${c.sent} sent`}
                          className="h-5 min-w-3 rounded-sm"
                          style={{ background: n ? `rgba(99,102,241,${0.15 + 0.85 * (n / heat.max)})` : '#f9fafb' }}
                        />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default InboxAnalyticsPage;
